import React, { useState, useEffect, useContext } from 'react';
import { Form, Button, Card, Alert, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';

const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    hourlyWage: '',
    isActive: true
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const { 
    firstName, 
    lastName, 
    phone, 
    email, 
    address, 
    city, 
    state, 
    zipCode, 
    hourlyWage,
    isActive
  } = formData;
  
  // Fetch employee data
  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await fetch(`/api/employees/${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch employee details');
        }
        
        const data = await response.json();
        
        // Format hourly wage with commas for display
        const formattedWage = parseFloat(data.HourlyWage).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
        
        setFormData({
          firstName: data.FirstName,
          lastName: data.LastName,
          phone: data.Phone || '',
          email: data.Email || '',
          address: data.Address || '',
          city: data.City || '',
          state: data.State || '',
          zipCode: data.ZipCode || '',
          hourlyWage: formattedWage,
          isActive: data.IsActive
        });
      } catch (err) {
        console.error('Error fetching employee:', err);
        setError('Failed to load employee details');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEmployee();
  }, [id, token]);
  
  const onChange = e => {
    let value = e.target.value;
    
    if (e.target.name === 'hourlyWage') {
      // Remove all non-digit and non-decimal characters
      let cleanValue = value.replace(/[^\d.]/g, '');
      
      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      if (parts.length > 2) {
        cleanValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Limit decimal places to 2
      if (parts.length > 1 && parts[1].length > 2) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
      
      // Format with commas for display
      if (cleanValue && !isNaN(cleanValue)) {
        const numericValue = parseFloat(cleanValue);
        if (!isNaN(numericValue)) {
          value = numericValue.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          });
        } else {
          value = cleanValue;
        }
      } else {
        value = cleanValue;
      }
    } else if (e.target.name === 'isActive') {
      value = e.target.checked;
    }
    
    setFormData({ ...formData, [e.target.name]: value });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!firstName || !lastName || !hourlyWage) {
      setError('First name, last name, and hourly wage are required');
      return;
    }
    
    // Validate hourly wage as numeric (remove commas first)
    const cleanHourlyWage = hourlyWage.replace(/,/g, '');
    if (isNaN(parseFloat(cleanHourlyWage))) {
      setError('Hourly wage must be a valid number');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`/api/employees/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          ...formData,
          hourlyWage: formData.hourlyWage.replace(/,/g, '')
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.msg || 'Failed to update employee');
      }
      
      toast.success('Employee updated successfully!');
      navigate(`/employees/${id}`);
    } catch (err) {
      console.error('Error updating employee:', err);
      setError(err.message || 'Failed to update employee');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading employee details...</div>;
  }
  
  return (
    <div className="form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Edit Employee</h2>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            <h4>Personal Information</h4>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={firstName}
                    onChange={onChange}
                    required
                    placeholder="Enter first name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={lastName}
                    onChange={onChange}
                    required
                    placeholder="Enter last name"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={phone}
                    onChange={onChange}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={email}
                    onChange={onChange}
                    placeholder="Enter email address"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <h4 className="mt-4">Address</h4>
            <Form.Group className="mb-3">
              <Form.Label>Street Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={address}
                onChange={onChange}
                placeholder="Enter street address"
              />
            </Form.Group>
            
            <Row>
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={city}
                    onChange={onChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={state}
                    onChange={onChange}
                    placeholder="Enter state"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    value={zipCode}
                    onChange={onChange}
                    placeholder="Enter zip code"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <h4 className="mt-4">Employment Information</h4>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Hourly Wage ($)</Form.Label>
                  <Form.Control
                    type="text"
                    name="hourlyWage"
                    value={hourlyWage}
                    onChange={onChange}
                    required
                    placeholder="Enter hourly wage (e.g., 25.50)"
                  />
                  <Form.Text className="text-muted">
                    Enter the amount as a number (e.g., 25.50)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="isActive"
                    name="isActive"
                    label="Active Employee"
                    checked={isActive}
                    onChange={onChange}
                  />
                  <Form.Text className="text-muted">
                    Inactive employees cannot be assigned to projects
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <div className="d-flex justify-content-between mt-4">
              <Button 
                variant="secondary" 
                onClick={() => navigate(`/employees/${id}`)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EditEmployee;
